
import React, { useEffect, useState } from 'react';
import { Box, TableBody, CircularProgress, IconButton, Tooltip } from '@mui/material';

import DeleteIcon from '@mui/icons-material/Delete';
import EditIcon from '@mui/icons-material/Edit';
import VisibilityIcon from '@mui/icons-material/Visibility';
import { useParams } from 'react-router-dom';
import {
  MainContainer,
  HeaderContainer,
  PageTitle,
  StyledTableContainer,
  StyledTable,
  StyledTableHead,
  StyledTableRow,
  StyledTableCell,
  CreateButton,
} from './styles';

import AddCustomFormFieldModal from './components/AddCustomFormFieldModal';
import CustomFormFieldPreviewModal from './components/CustomFormFieldPreviewModal';
import { useBreadcrumb } from '../../../../context/BreadcrumbContext';
import { useSidebar } from '../../../../context/SidebarContext';
import { useDispatch, useSelector } from 'react-redux';
import { AppDispatch } from '../../../../store';

import {
  selectCustomFormFields,
  selectGetCustomFormFieldsLoading,
  selectGetCustomFormFieldsError,
  selectDeleteCustomFormFieldLoading,
  selectDeleteCustomFormFieldSuccess,
  selectDeleteCustomFormFieldError,
  selectUpdateCustomFormFieldSuccess,
  selectUpdateCustomFormFieldError,
  selectAddCustomFormFieldSuccess,
  selectAddCustomFormFieldError
} from '../../../../modules/masterdata/selectors';
import { ICustomFormField } from '../../../../modules/masterdata/types';
import { deleteCustomFormField, getCustomFormFields } from '../../../../modules/masterdata/actions';
import { resetDeleteCustomFormFieldState, resetUpdateCustomFormFieldState, resetAddCustomFormFieldState } from '../../../../modules/masterdata/reducers';
import ConfirmationDialog from '../../../../components/common/ConfirmationDialog';
import AlertPopup from '../../../../components/common/AlertPopup';

const ManageCustomFormFieldsPage: React.FC = () => {
  const { id } = useParams<{ id: string }>();
  const { setActivePage } = useSidebar();
  const { setPath, setIsShow } = useBreadcrumb();
  const dispatch = useDispatch<AppDispatch>();

  const fields = useSelector(selectCustomFormFields);
  const loading = useSelector(selectGetCustomFormFieldsLoading);
  const error = useSelector(selectGetCustomFormFieldsError);
  const deleteLoading = useSelector(selectDeleteCustomFormFieldLoading);
  const deleteSuccess = useSelector(selectDeleteCustomFormFieldSuccess);
  const deleteError = useSelector(selectDeleteCustomFormFieldError);
  const updateSuccess = useSelector(selectUpdateCustomFormFieldSuccess);
  const updateError = useSelector(selectUpdateCustomFormFieldError);
  const addSuccess = useSelector(selectAddCustomFormFieldSuccess);
  const addError = useSelector(selectAddCustomFormFieldError);

  const [alert, setAlert] = useState<{ message: string, severity: 'success' | 'error' } | null>(null);
  const [openModal, setOpenModal] = useState(false);
  const [deleteConfirmationOpen, setDeleteConfirmationOpen] = useState(false);
  const [fieldToDelete, setFieldToDelete] = useState<number | null>(null);
  const [fieldToEdit, setFieldToEdit] = useState<ICustomFormField | null>(null);
  const [previewModalOpen, setPreviewModalOpen] = useState(false);
  const [fieldToPreview, setFieldToPreview] = useState<ICustomFormField | null>(null);

  useEffect(() => {
    setActivePage('custom-form');
    setPath([
      { title: 'Settings', path: '/master-data' },
      { title: 'Master Data', path: '/master-data' },
      { title: 'Custom Forms', path: '/master-data/custom-forms' },
      { title: 'Manage Fields', path: '#' },
    ]);
    setIsShow(true);

    if (id) {
      dispatch(getCustomFormFields(Number(id)));
    }
  }, [setActivePage, setPath, setIsShow, dispatch, id]);

  useEffect(() => {
    if (error) {
      setAlert({ message: error, severity: 'error' });
    }
  }, [error]);

  useEffect(() => {
    if (deleteSuccess) {
      setAlert({ message: 'Field deleted successfully', severity: 'success' });
      dispatch(resetDeleteCustomFormFieldState());
      if (id) {
        dispatch(getCustomFormFields(Number(id)));
      }
      setDeleteConfirmationOpen(false);
    }
    if (deleteError) {
      setAlert({ message: deleteError, severity: 'error' });
      dispatch(resetDeleteCustomFormFieldState());
    }
  }, [deleteSuccess, deleteError, dispatch, id]);

  useEffect(() => {
    if (updateSuccess) {
      setAlert({ message: 'Field updated successfully', severity: 'success' });
      dispatch(resetUpdateCustomFormFieldState());
      if (id) {
        dispatch(getCustomFormFields(Number(id)));
      }
      setOpenModal(false);
      setFieldToEdit(null);
    }
    if (updateError) {
      setAlert({ message: updateError, severity: 'error' });
      dispatch(resetUpdateCustomFormFieldState());
    }
  }, [updateSuccess, updateError, dispatch, id]);

  useEffect(() => {
    if (addSuccess) {
      setAlert({ message: 'Field added successfully', severity: 'success' });
      dispatch(resetAddCustomFormFieldState());
      if (id) {
        dispatch(getCustomFormFields(Number(id)));
      }
      setOpenModal(false);
    }
    if (addError) {
      setAlert({ message: addError, severity: 'error' });
      dispatch(resetAddCustomFormFieldState());
    }
  }, [addSuccess, addError, dispatch, id]);

  const handleDeleteClick = (fieldId: number) => {
    setFieldToDelete(fieldId);
    setDeleteConfirmationOpen(true);
  };

  const handleEditClick = (field: ICustomFormField) => {
    setFieldToEdit(field);
    setOpenModal(true);
  }

  const handleCloseModal = () => {
    setOpenModal(false);
    setFieldToEdit(null);
  }

  const handlePreviewClick = (field: ICustomFormField) => {
    setFieldToPreview(field);
    setPreviewModalOpen(true);
  }

  const handleClosePreview = () => {
    setPreviewModalOpen(false);
    setFieldToPreview(null);
  }

  const handleConfirmDelete = () => {
    if (fieldToDelete) {
      dispatch(deleteCustomFormField(fieldToDelete));
    }
  };

  return (
    <Box>
      {alert && <AlertPopup message={alert.message} severity={alert.severity} open={!!alert} onClose={() => setAlert(null)} />}
      <MainContainer>
        <HeaderContainer>
          <PageTitle variant="h6">Custom Form Fields</PageTitle>
          <CreateButton onClick={() => setOpenModal(true)}>Add Field</CreateButton>
        </HeaderContainer>

        <AddCustomFormFieldModal
          open={openModal}
          onClose={handleCloseModal}
          formId={Number(id)}
          fieldToEdit={fieldToEdit}
        />

        <CustomFormFieldPreviewModal
          open={previewModalOpen}
          onClose={handleClosePreview}
          field={fieldToPreview}
        />

        <StyledTableContainer>
          <StyledTable>
            <StyledTableHead>
              <StyledTableRow>
                <StyledTableCell>Id</StyledTableCell>
                <StyledTableCell>Field Name</StyledTableCell>
                <StyledTableCell>Field Type</StyledTableCell>
                <StyledTableCell>Required</StyledTableCell>
                <StyledTableCell>Action</StyledTableCell>
              </StyledTableRow>
            </StyledTableHead>
            <TableBody>
              {loading ? (
                <StyledTableRow>
                  <StyledTableCell colSpan={5} align="center">
                    <CircularProgress />
                  </StyledTableCell>
                </StyledTableRow>
              ) : fields && fields.length > 0 ? (
                fields.map((field: ICustomFormField, index: number) => (
                  <StyledTableRow key={field.field_id}>
                    <StyledTableCell>{field.field_id}</StyledTableCell>
                    <StyledTableCell>{field.field_name}</StyledTableCell>
                    <StyledTableCell>{field.field_type}</StyledTableCell>
                    <StyledTableCell>{field.is_required ? 'Yes' : 'No'}</StyledTableCell>

                    <StyledTableCell>
                      <Tooltip title="Delete">
                        <IconButton onClick={() => handleDeleteClick(field.field_id)} color="error">
                          <DeleteIcon />
                        </IconButton>
                      </Tooltip>
                      <Tooltip title="Edit">
                        <IconButton onClick={() => handleEditClick(field)} color="primary">
                          <EditIcon />
                        </IconButton>
                      </Tooltip>
                      <Tooltip title="Preview">
                        <IconButton onClick={() => handlePreviewClick(field)} color="info">
                          <VisibilityIcon />
                        </IconButton>
                      </Tooltip>
                    </StyledTableCell>
                  </StyledTableRow>
                ))
              ) : (
                <StyledTableRow>
                  <StyledTableCell colSpan={5} align="center">No fields found for this form.</StyledTableCell>
                </StyledTableRow>
              )}
            </TableBody>
          </StyledTable>
        </StyledTableContainer>
      </MainContainer>
      <ConfirmationDialog
        open={deleteConfirmationOpen}
        title="Delete Field"
        message="Are you sure you want to delete this field? This action cannot be undone."
        onConfirm={handleConfirmDelete}
        onClose={() => setDeleteConfirmationOpen(false)}
        loading={deleteLoading}
      />
    </Box>
  );
};

export default ManageCustomFormFieldsPage;
